import React, { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';
import styles from './Input.module.css';

/**
 * Reusable Input Component with validation support
 *
 * @param {Object} props
 * @param {string} props.label - Input label
 * @param {string} props.type - Input type (text, email, password, number, etc.)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text below input
 * @param {boolean} props.required - Required field indicator
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.fullWidth - Full width input
 * @param {React.ReactNode} props.leftIcon - Icon on the left
 * @param {React.ReactNode} props.rightIcon - Icon on the right
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Input = forwardRef(({
  label,
  type = 'text',
  placeholder = '',
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  size = 'md',
  className = '',
  id,
  name,
  value,
  onChange,
  onBlur,
  ...rest
}, ref) => {
  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    styles.wrapper,
    fullWidth && styles.fullWidth,
    className
  ].filter(Boolean).join(' ');

  const inputContainerClasses = [
    styles.inputContainer,
    styles[size],
    error && styles.hasError,
    disabled && styles.disabled,
    leftIcon && styles.hasLeftIcon,
    (rightIcon || error) && styles.hasRightIcon
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={inputContainerClasses}>
        {leftIcon && (
          <span className={styles.iconLeft}>{leftIcon}</span>
        )}

        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={styles.input}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          {...rest}
        />

        {error ? (
          <span className={styles.iconRight}>
            <AlertCircle size={18} className={styles.errorIcon} />
          </span>
        ) : rightIcon ? (
          <span className={styles.iconRight}>{rightIcon}</span>
        ) : null}
      </div>

      {error && (
        <p id={`${inputId}-error`} className={styles.errorText}>
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={`${inputId}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
